import React, { useEffect } from "react";
import { Box, Paper, Alert, IconButton, Tooltip, Button } from "@mui/material";
import { Refresh as RefreshIcon, History as HistoryIcon } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import ChatHeader from "./ChatHeader";
import MessagesList from "./MessagesList";
import MessageInput from "./MessageInput";
import TypingIndicator from "./TypingIndicator";
import ShikaLogo from "./ShikaLogo";
import { useChatState } from '../hooks';

const SimpleChatContainer = () => {
    const navigate = useNavigate();

    const {
        // State
        messages,
        currentSession,
        isLoading,
        isTyping,
        error,
        isInitialized,

        // Actions
        initializeChat,
        sendMessage,
        startNewSession,
        retryLastMessage,

        // Computed values
        canSendMessage,
    } = useChatState();

    // Initialize chat when component mounts
    useEffect(() => {
        initializeChat();
    }, [initializeChat]);

    const handleSendMessage = async (message) => {
        await sendMessage(message);
    };

    const handleStartNewSession = () => {
        startNewSession();
    };

    const handleRetry = () => {
        retryLastMessage();
    };

    const handleViewAllChats = () => {
        navigate('/chat');
    };

    return (
        <Box sx={{ 
            height: "100%", 
            display: "flex", 
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0  // Critical for flexbox overflow
        }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    height: "100%", 
                    display: "flex", 
                    flexDirection: "column",
                    overflow: "hidden",
                    borderRadius: 2,
                    minHeight: 0  // Critical for flexbox overflow
                }}
            >
                {/* Custom Header for Simple Chat */}
                <Box sx={{ 
                    p: 2, 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'primary.main',
                    color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ShikaLogo size={32} />
                        <Box>
                            <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                Shika AI
                            </Box>
                            <Box sx={{ fontSize: '0.875rem', opacity: 0.8 }}>
                                Quick chat - for full history go to chat page
                            </Box>
                        </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View all chats">
                            <IconButton 
                                size="small" 
                                onClick={handleViewAllChats}
                                sx={{ color: 'white' }}
                            >
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="New session">
                            <IconButton 
                                size="small" 
                                onClick={handleStartNewSession}
                                sx={{ color: 'white' }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                
                <Box sx={{ 
                    flex: 1, 
                    display: "flex", 
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0  // Critical for flexbox overflow
                }}>
                    {/* Error Display */}
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ m: 1, mb: 0 }}
                            action={
                                <Tooltip title="Retry last message">
                                    <IconButton
                                        size="small"
                                        onClick={handleRetry}
                                        color="inherit"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Messages List */}
                    <MessagesList 
                        messages={messages} 
                        isLoading={isLoading && !isInitialized}
                    />
                    
                    {/* Typing Indicator */}
                    {isTyping && <TypingIndicator />}
                </Box>
                
                {/* Message Input */}
                <MessageInput 
                    onSendMessage={handleSendMessage}
                    disabled={!canSendMessage}
                    placeholder={
                        !isInitialized 
                            ? "Initializing chat..." 
                            : "Type your message... (Enter to send, Shift+Enter for new line)"
                    }
                />
            </Paper>
        </Box>
    );
};

export default SimpleChatContainer; 